import { useFormik } from 'formik';
import moment, { Moment } from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import * as yup from 'yup';

import useHttp from 'app/utils/hooks/use-http';

import * as fromApp from '../../../store/app';

import ByLineView from './ByLine.view';

import { Clients } from 'app/enums/clients';

import { FormValidationObject } from 'app/models/general';
import { IShuttle, IShuttleStop, IShuttleTime, IShuttleTimeStop } from 'app/models/shuttle';
import { SelectOption } from 'app/ui/MNewSelect/MNewSelect.model';
import { useTranslation } from 'react-i18next';
import { ValueType } from 'react-select';
import { TimeFormats } from '../../../constants/TimeFormat';
import { ByLineForm, IDropOff, IInitTimes, IPickUp, IPickUpTime } from './ByLine.model';

import { RoutingType } from 'app/enums/shuttleRoutingType';
import { IHoliday } from 'app/models/holiday';
import { ISupplier } from 'app/models/supplier';
import classes from './ByLine.module.scss';
import { findShuttle, supportOverHour } from './ByLine.utils';
interface PropsFromState {
  client: Clients;
  supplier: ISupplier | null;
}

interface Props extends PropsFromState {
  passengers: number;
  onSubmit: (values: ByLineForm, shuttleType: 'simple' | 'fixable', shuttle: IShuttle) => void;
}

const ByLine: React.FC<Props> = (props: React.PropsWithChildren<Props>) => {
  const { t } = useTranslation();

  const http = useHttp();

  const [shuttlesState, setShuttlesState] = useState<IShuttle[]>([]);
  const [allShuttlesState, setAllShuttlesState] = useState<IShuttle[]>([]);
  const [areaOptionsState, setAreaOptionsState] = useState<SelectOption[]>([]);
  const [lineOptionsState, setLineOptionsState] = useState<SelectOption[]>([]);
  const [directionOptionsState, setDirectionOptionsState] = useState<SelectOption[]>([]);
  const [weekDaysState, setWeekDaysState] = useState<number[]>([]);
  const [pickUpsStopsState, setPickUpStopsState] = useState<SelectOption<IPickUp>[]>([]);
  const [pickUpStopsTimesState, setPickUpStopsTimesState] = useState<SelectOption<IPickUpTime>[]>([]);
  const [dropoffStopsState, setDropoffStopsState] = useState<IDropOff[]>([]);
  const [shuttleTypeState, setShuttleTypeState] = useState<'simple' | 'fixable'>('simple');
  const [minutesBeforeState, setMinutesBeforeState] = useState<number>(0);
  const [holidaysState, setHolidaysState] = useState<IHoliday[]>([]);

  const alertMessage = useCallback((message) => {
    alert(`\n${message}`);
  }, []);

  const form = useFormik<ByLineForm>({
    initialValues: {
      area: null,
      line: null,
      direction: null,
      date: null,
      pickUp: null,
      pickUpTime: null,
      dropOff: null,
      dropOffTime: null,
      passengers: props.passengers,
      initialTime: null,
    },
    validationSchema: yup.object<FormValidationObject<keyof ByLineForm>>({
      area: yup.string().nullable(),
      line: yup.string().nullable().required(),
      direction: yup.string().nullable().required(),
      date: yup.object().required(),
      pickUp: yup.object().nullable().required(),
      pickUpTime: shuttleTypeState === 'simple' ? yup.object().nullable().required() : yup.object().nullable(),
      dropOff: yup.object().nullable().required(),
      dropOffTime: yup.string().nullable().required(),
      passengers: yup.number(),
      initialTime: yup.mixed().nullable(),
    }),
    onSubmit(values: ByLineForm) {
      const shuttle = findShuttle(shuttlesState, holidaysState, values);

      const holidayCode =
        holidaysState.find((holiday: IHoliday) => holiday.date === values.date?.format(TimeFormats.ServerFormatDate))?.code || -1;

      if (shuttleTypeState === 'fixable') {
        const ranges: { start: moment.Moment; end: moment.Moment }[] = [];
        let availableTimes = '';
        shuttle!.times.forEach((time: IShuttleTime) => {
          if (values.date && (time.weekDays.includes(values.date?.weekday()) || time.weekDays.includes(holidayCode))) {
            ranges.push({ start: moment(time.hour1, TimeFormats.Time24), end: moment(time.hour2, TimeFormats.Time24) });
            availableTimes += `${time.hour1} - ${time.hour2}\n`;
          }
        });

        const daysRange = ranges.some((range: { start: moment.Moment; end: moment.Moment }) => {
          const bookTime = moment(values.dropOffTime, TimeFormats.Time24);
          return bookTime.isBetween(range.start, range.end);
        });
        if (daysRange) {
          props.onSubmit(values, shuttleTypeState, shuttle!);
        } else {
          alertMessage(t('reservationBar.message', { times: availableTimes }));
        }
      } else {
        props.onSubmit(values, shuttleTypeState, shuttle!);
      }
    },
  });

  useEffect(() => {
    form.setValues((prev: ByLineForm) => {
      return {
        ...prev,
        pickUp: null,
        pickUpTime: null,
        dropOff: null,
        dropOffTime: null,
      };
    });
  }, [form.setValues, form.values.initialTime]);

  const dropoffOptions = useMemo(() => {
    return dropoffStopsState
      .filter((stop: IDropOff) => {
        return !form.values.pickUp?.zone || form.values.pickUp.zone !== stop.zone;
      })
      .map((stop: IDropOff) => {
        return { label: stop.name, value: stop };
      });
  }, [dropoffStopsState, form.values.pickUp]);

  const handleAreaChange = useCallback(
    (event: ValueType<SelectOption, false>) => {
      const temp: IShuttle[] = [];

      const shuttles = allShuttlesState.filter((shuttle: IShuttle) => shuttle.area === event?.value);

      for (const shuttle of shuttles) {
        if (temp.find((shutt: IShuttle) => shutt.line === shuttle.line)) {
          continue;
        }
        temp.push(shuttle);
      }

      setLineOptionsState(() => temp.map((shuttle: IShuttle) => ({ value: shuttle.line, label: shuttle.line })));

      form.setValues((prev: ByLineForm) => {
        return {
          ...prev,
          line: null,
          direction: null,
          date: null,
          pickUp: null,
          pickUpTime: null,
          dropOff: null,
          dropOffTime: null,
        };
      });

      setPickUpStopsState(() => []);
      setPickUpStopsTimesState(() => []);
      setDropoffStopsState(() => []);
    },
    [allShuttlesState, setLineOptionsState],
  );

  const handleLineChange = useCallback(
    (event: ValueType<SelectOption, false>) => {
      const shuttles = allShuttlesState.filter(
        (shuttle: IShuttle) => shuttle.line === event?.value && (shuttle.area === form.values.area || !shuttle.area),
      );

      const temp: IShuttle[] = [];

      for (const shuttle of shuttles) {
        if (temp.find((shutt: IShuttle) => shutt.direction === shuttle.direction)) {
          continue;
        }
        temp.push(shuttle);
      }

      setDirectionOptionsState(() =>
        temp.map((shuttle: IShuttle) => {
          return { value: shuttle.direction, label: shuttle.direction };
        }),
      );

      form.setValues((prev: ByLineForm) => {
        return {
          ...prev,
          direction: null,
          date: null,
          pickUp: null,
          pickUpTime: null,
          dropOff: null,
          dropOffTime: null,
        };
      });

      setPickUpStopsState(() => []);
      setPickUpStopsTimesState(() => []);
      setDropoffStopsState(() => []);
    },
    [form.setValues, allShuttlesState, setDirectionOptionsState, form.values.area],
  );

  const handleDirectionChange = useCallback(
    (event: ValueType<SelectOption, false>) => {
      if (form.values.line != null && event != null) {
        http.shuttle.getShuttle(props.client, {}, { line: form.values.line, direction: event.value }, props.supplier?.name).then((responseShuttles: IShuttle[]) => {
          const shuttles = responseShuttles;

          setShuttlesState(() => supportOverHour(shuttles));

          const weekDays = shuttles.reduce((acc: number[], curr: IShuttle) => {
            const days = curr.times.reduce((acc: number[], curr: IShuttleTime) => {
              return [...acc, ...curr.weekDays];
            }, []);

            return [...acc, ...days];
          }, []);

          setWeekDaysState(() => weekDays);
        });
      }

      form.setValues((prev: ByLineForm) => {
        return {
          ...prev,
          date: null,
          pickUp: null,
          pickUpTime: null,
          dropOff: null,
          dropOffTime: null,
        };
      });

      setPickUpStopsState(() => []);
      setPickUpStopsTimesState(() => []);
      setDropoffStopsState(() => []);
    },
    [form.setValues, form.values.line, setWeekDaysState, setShuttleTypeState],
  );

  const handlerDateChange = useCallback(
    (value: moment.Moment | null) => {
      if (!value) {
        return;
      }

      const selectedWeekday = value?.weekday();

      const holidayCode =
        holidaysState.find((holiday: IHoliday) => holiday.date === value.format(TimeFormats.ServerFormatDate))?.code || -1;

      const shuttle = findShuttle(shuttlesState, holidaysState, { ...form.values, date: value });

      form.setValues((prev: ByLineForm) => {
        return {
          ...prev,
          pickUp: null,
          pickUpTime: null,
          dropOff: null,
          dropOffTime: null,
        };
      });

      setPickUpStopsTimesState(() => []);
      setDropoffStopsState(() => []);

      setShuttleTypeState(() => {
        if (shuttle?.times && shuttle.times.length != 0 && shuttle.times[0].hour1 && shuttle.times[0].hour2) {
          return 'fixable';
        }
        return 'simple';
      });

      if (selectedWeekday != undefined && shuttle) {
        try {


          const pickUpStops = shuttle.times.reduce((accTime: SelectOption<IPickUp>[], time: IShuttleTime, indexTime: number) => {
            if (time.weekDays.includes(selectedWeekday) || time.weekDays.includes(holidayCode)) {
              const stmFirstStop = time.stops?.length ? time.stops[0]?.zone : null;

              const stops: SelectOption<IPickUp>[] = time.stops.reduce(
                (accStop: SelectOption<IPickUp>[], stop: IShuttleTimeStop, indexStop: number) => {
                  if (indexStop + 1 === time.stops.length || (shuttleTypeState === 'simple' && stop.dur === null)) {
                    return accStop;
                  }

                  if (shuttle.supplier?.toLowerCase() === 'STM'.toLowerCase() && stmFirstStop != null && stmFirstStop !== stop.zone) {
                    return accStop;
                  }

                  const originalDate: Moment = value.clone();

                  if (shuttle.times[indexTime].overHours) {
                    originalDate.add(1, 'day');
                  }

                  const stopTime: Moment = moment(
                    shuttleTypeState === 'simple' ? shuttle.times[indexTime].hour : shuttle.times[indexTime].hour1,
                    TimeFormats.Time24,
                  );

                  return [
                    ...accStop,
                    {
                      value: {
                        addr: stop.addr,
                        lat: Number(stop.lat),
                        lon: Number(stop.lon),
                        date: originalDate.set({ hour: stopTime.hours(), minute: stopTime.minutes() }).add(stop.dur, 'seconds'),
                        stopIndex: indexStop,
                        zone: stop.zone,
                        timeIndex: indexTime,
                      },
                      label: stop.name,
                    },
                  ];
                },
                [],
              );
              return [...accTime, ...stops];
            }
            return accTime;
          }, []);

          setPickUpStopsState(() => pickUpStops);
        } catch (e) {
          console.error(e);
        }
      }
    },
    [shuttlesState, form.values.direction, form.values.line, setPickUpStopsState, shuttleTypeState, holidaysState],
  );

  const shuttleExoType = useMemo(() => {
    const shuttle = findShuttle(shuttlesState, holidaysState, form.values);

    if (!shuttle || shuttle.routingType !== RoutingType.Exo) {
      return null;
    }

    return shuttle;
  }, [shuttlesState, holidaysState, form.values]);

  const handlePickUpChange = useCallback(
    (value: ValueType<SelectOption<IPickUp>, false>) => {
      const shuttle = findShuttle(shuttlesState, holidaysState, form.values);

      form.setValues((prev: ByLineForm) => {
        return {
          ...prev,
          pickUpTime: null,
          dropOff: null,
          dropOffTime: null,
        };
      });

      if (shuttleExoType) {
        const type = shuttleExoType.times[form.values.initialTime!.timeIndex].type;
        const alone = shuttleExoType.times[form.values.initialTime!.timeIndex].stops[value!.value.stopIndex].alone;

        const nextTime = form.values.initialTime!.time.clone();

        if (type === 'departure') {
          nextTime.add({ seconds: alone ? alone * 1.5 : 0 });
        } else if (type === 'arrival') {
          nextTime.subtract({ seconds: alone ? alone * 1.5 : 0 });
        }

        const days: number | null =
          nextTime.diff(form.values.date, 'days') || shuttleExoType.times[form.values.initialTime!.timeIndex].overHours ? 1 : null;

        const time = {
          value: {
            stopIndex: value!.value.stopIndex,
            timeIndex: form.values.initialTime!.timeIndex,
            time: nextTime,
          },
          label: (
            <div className={classes['addDayContainer']}>
              <span>{nextTime.format(TimeFormats.Time24)}</span>
              {days ? <span className={classes['addDayContainer__subLabel']}>{t('reservationBar.days', { days })}</span> : null}
            </div>
          ),
        };

        setPickUpStopsTimesState(() => [time]);
      } else {
        setPickUpStopsTimesState(() =>
          pickUpsStopsState.reduce((acc: SelectOption<IPickUpTime>[], pickUps: SelectOption<IPickUp>) => {
            if (pickUps.value.stopIndex !== value?.value.stopIndex) {
              return acc;
            }

            const days: number | null =
              pickUps.value.date?.diff(form.values.date, 'days') || shuttle?.times[pickUps.value.timeIndex].overHours ? 1 : null;

            return [
              ...acc,
              {
                isDisabled: moment().isAfter(pickUps.value.date?.clone().add(-minutesBeforeState, 'minutes')),
                value: {
                  stopIndex: pickUps.value.stopIndex,
                  timeIndex: pickUps.value.timeIndex,
                  time: pickUps.value.date,
                },
                label: (
                  <div className={classes['addDayContainer']}>
                    <span>{pickUps.value.date!.format(TimeFormats.Time24)}</span>
                    {days ? <span className={classes['addDayContainer__subLabel']}>{t('reservationBar.days', { days })}</span> : null}
                  </div>
                ),
              },
            ];
          }, []),
        );
      }

      if (shuttleTypeState === 'fixable' && value) {
        const dropOffStops =
          shuttle?.stops.reduce((acc: IDropOff[], stop: IShuttleStop, index: number) => {
            if (value && value.value.stopIndex >= index) {
              return acc;
            }

            return [
              ...acc,
              {
                addr: stop.addr,
                name: stop.name,
                lat: Number(stop.lat),
                lon: Number(stop.lon),
                date: null,
                zone: stop.zone,
                stopIndex: index,
              },
            ];
          }, []) || [];
        setDropoffStopsState(() => dropOffStops);
      }
    },
    [
      shuttlesState,
      minutesBeforeState,
      form.values.direction,
      form.values.line,
      form.values.date,
      form.values.pickUp,
      form.values.initialTime,
      pickUpsStopsState,
      setPickUpStopsTimesState,
      setDropoffStopsState,
      form.setValues,
      shuttleTypeState,
      holidaysState,
      shuttleExoType,
      t,
    ],
  );

  const handlePickUpTimeChange = useCallback(
    (value: SelectOption<IPickUpTime> | null) => {
      if (!value) {
        return;
      }

      const shuttle = findShuttle(shuttlesState, holidaysState, form.values);

      form.setValues((prev: ByLineForm) => {
        return {
          ...prev,
          dropOff: null,
          dropOffTime: null,
        };
      });

      const timeIndex = value.value.timeIndex;
      const stopIndex = value.value.stopIndex;

      if (shuttle && timeIndex != null && timeIndex != -1 && stopIndex != null && form.values.pickUp && form.values.date) {
        const dropOffStops = shuttle.times[timeIndex].stops.reduce((acc: IDropOff[], stop: IShuttleTimeStop, indexStop) => {

          //out if the same zone
          if (stopIndex < indexStop && stop.dur !== null) {
            const originalDate: Moment = form.values.date!.clone();

            const stopTime: Moment =
              shuttleTypeState === 'simple'
                ? moment(shuttle.times[timeIndex].hour, TimeFormats.Time24).add(stop.dur, 'seconds')
                : moment(shuttle.times[timeIndex].hour1, TimeFormats.Time24).add(stop.dur, 'seconds');

            const dropOffTime: Moment = originalDate.set({ hour: stopTime.hours(), minute: stopTime.minutes() });

            if (!dropOffTime.isAfter(form.values.pickUp?.date)) {
              dropOffTime.add({ day: 1 });
            }

            return [
              ...acc,
              {
                addr: stop.addr,
                name: stop.name,
                lat: Number(stop.lat),
                lon: Number(stop.lon),
                date: dropOffTime,
                zone: stop.zone,
                stopIndex: indexStop,
              },
            ];
          }
          return acc;
        }, []);
        setDropoffStopsState(() => dropOffStops);
      }
    },
    [
      shuttlesState,
      form.values.pickUpTime,
      form.values.pickUp,
      form.values.line,
      form.values.direction,
      form.values.date,
      setDropoffStopsState,
      pickUpsStopsState,
      shuttleTypeState,
      holidaysState,
    ],
  );

  const handleDropOffChange = useCallback(
    (value: ValueType<SelectOption<IDropOff>, false>) => {
      if (shuttleExoType) {
        const type = shuttleExoType.times[form.values.initialTime!.timeIndex].type;
        const alone = shuttleExoType.times[form.values.initialTime!.timeIndex].stops[value!.value.stopIndex].alone;

        const nextTime = form.values.initialTime!.time.clone();

        if (type === 'departure') {
          nextTime.add({ seconds: alone ? alone * 1.5 : 0 });
        } else if (type === 'arrival') {
          nextTime.subtract({ seconds: alone ? alone * 1.5 : 0 });
        }

        form.setFieldValue('dropOffTime', nextTime.format(TimeFormats.Time24));
      } else {
        form.setFieldValue('dropOffTime', value!.value.date!.format(TimeFormats.Time24));
      }
    },
    [form.values.initialTime, shuttleExoType, form.setFieldValue],
  );

  useEffect(() => {
    form.setFieldValue('passengers', props.passengers);
  }, [props.passengers, form.setFieldValue]);

  useEffect(() => {
    http.shuttle.getShuttle(props.client, {
      area: 1,
      code: 1,
      direction: 1,
      line: 1,
      name: 1,
      pax: 1,
      profile: 1,
      profileCode: 1,
      profileName: 1,
      routingType: 1,
      supplier: 1,
      unit: 1,
      paymentTypes: 1,
      minutesBefore: 1,
      fare: 1,
    }, {}, props.supplier?.name).then((responseShuttles: IShuttle[]) => {
      const shuttles = responseShuttles;

      setAllShuttlesState(() => shuttles);

      if (shuttles.length > 0) {
        setMinutesBeforeState(() => shuttles[0].minutesBefore || 0);
      }

      const areasOptions = shuttles.reduce((acc: SelectOption[], current: IShuttle) => {
        if (!current.area) {
          return acc;
        }

        if (acc.find((value: SelectOption) => value.value === current.area)) {
          return acc;
        }

        return [...acc, { value: current.area, label: current.area }];
      }, []);

      setAreaOptionsState(() => areasOptions);

      //if there is not one shuttle with area, we dont show Select areas, and build options line here, without filters of areas
      if (areasOptions.length === 0) {
        const temp: IShuttle[] = [];

        for (const shuttle of shuttles) {
          if (temp.find((shutt: IShuttle) => shutt.line === shuttle.line)) {
            continue;
          }
          temp.push(shuttle);
        }

        setLineOptionsState(() => temp.map((shuttle: IShuttle) => ({ value: shuttle.line, label: shuttle.line })));
      }
    });
  }, [props.client, props.supplier, setLineOptionsState, setAllShuttlesState, setAreaOptionsState]);

  useEffect(() => {
    for (let i = 0; i < 4; i++) {
      const fromDate = moment().set('date', 1).add(i, 'month');

      const toDate = moment()
        .set('date', 1)
        .add(i + 1, 'month');

      http.holiday.getByDates(fromDate.format(TimeFormats.ServerFormatDate), toDate.format(TimeFormats.ServerFormatDate)).then((data) => {
        setHolidaysState((prev: IHoliday[]) => {
          return [...prev, ...data];
        });
      });
    }
  }, []);

  const initialStop = useMemo(() => {
    if (!shuttleExoType) {
      return '';
    }

    if (shuttleExoType.times[0].type === 'departure') {
      return t('reservationBar.formLine.departingFrom', { stop: shuttleExoType.stops[0].name });
    }

    return t('reservationBar.formLine.arrivingAt', { stop: shuttleExoType.stops[shuttleExoType.stops.length - 1].name });
  }, [shuttleExoType, t]);

  const initTimePlaceHolder = useMemo(() => {
    if (!shuttleExoType) {
      return '';
    }

    if (shuttleExoType.times[0].type === 'departure') {
      return t('reservationBar.formLine.departureTime');
    }

    return t('reservationBar.formLine.arrivalTime');
  }, [shuttleExoType, t]);

  const initalTimesOptions: SelectOption<IInitTimes>[] = useMemo(() => {
    if (!shuttleExoType || !form.values.date) {
      return [];
    }

    return shuttleExoType.times.map((time: IShuttleTime, index: number) => {
      const [hours, minutes] = time.hour.split(':');

      const initTime = form.values.date!.clone().set({ hours: Number(hours || 0), minutes: Number(minutes || 0) });

      return {
        value: {
          timeIndex: index,
          time: initTime,
        },
        label: time.hour,
      };
    });
  }, [shuttleExoType, form.values.date]);

  return (
    <ByLineView
      form={form}
      shuttleType={shuttleTypeState}
      optionsArea={areaOptionsState}
      optionsLine={lineOptionsState}
      optionsDirection={directionOptionsState}
      optionsPickupStops={pickUpsStopsState.reduce((acc: SelectOption<IPickUp>[], curr: SelectOption<IPickUp>) => {
        if (acc.find((item: SelectOption<IPickUp>) => item.value.addr === curr.value.addr)) {
          return acc;
        }
        return [...acc, curr];
      }, [])}
      optionsPickUpStopsTimes={pickUpStopsTimesState}
      optionsDropoffStop={dropoffOptions}
      disabledWeekDaysState={weekDaysState}
      holidays={
        weekDaysState.length > 0 ? holidaysState.map((holiday: IHoliday) => moment(holiday.date, TimeFormats.ServerFormatDate)) : []
      }
      initialStop={initialStop}
      initTimePlaceHolder={initTimePlaceHolder}
      isExoRoutingType={!!shuttleExoType}
      initalTimesOptions={initalTimesOptions}
      onAreaChange={handleAreaChange}
      onLineChange={handleLineChange}
      onDirectionChange={handleDirectionChange}
      onDateChange={handlerDateChange}
      onPickUpChange={handlePickUpChange}
      onPickUpTimeChange={handlePickUpTimeChange}
      onDropOffChange={handleDropOffChange}
    >
      {props.children}
    </ByLineView>
  );
};

ByLine.displayName = 'ByLine';
ByLine.defaultProps = {};

const mapStateToProps = (state: fromApp.AppState) => {
  return {
    client: state.settings.specs?.client || Clients.Moovex,
    supplier: state.settings.supplierDetails,
    paymentTypes: state.settings.specs?.paymentTypes || null,
  };
};

export default connect(mapStateToProps)(ByLine);
